import {
  Button,
  Label,
  makeStyles,
  Tag,
  TagGroup,
} from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useGetFilters } from "../../hooks/useSearchFilters";
import { QueryFilter } from "../../models/QueryFilter";
import { cloneFilters } from "./filterValueUtils";
import { QueryFilterTagsSkeleton } from "../skeletons/QueryFilterTagsSkeleton";

interface QueryFilterTagsProps {
  selectedFilters: QueryFilter[];
  filtersEndpoint: string;
  onFiltersChange: (selectedFilters: QueryFilter[]) => void;
  displayMode: "row" | "grouped";
  disableQueryParamFiltering?: boolean;
}

const useStyles = makeStyles({
  queryFilterTagsContainer: {
    display: "flex",
    alignItems: "center",
    flexDirection: "row",
    gap: "15px",
    flexWrap: "wrap",
    padding: "10px",
  },
  queryFilterTagsContainerGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    flexWrap: "wrap",
    paddingTop: "20px",
  },
  tagGroupContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  tagGroupContainerGroup: {
    display: "flex",
    flexDirection: "column",
  },
  filterHeaderGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  tagGroup: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
  },
});

export const QueryFilterTags = (props: QueryFilterTagsProps) => {
  const classes = useStyles();
  const {
    selectedFilters,
    filtersEndpoint,
    onFiltersChange,
    displayMode,
    disableQueryParamFiltering,
  } = props;
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: filters, isLoading: loadingFilters } =
    useGetFilters(filtersEndpoint);

  const onDismissTag = (fieldUniqueName: string, value: string) => {
    const newSelectedFilters: QueryFilter[] = [];

    selectedFilters?.forEach((f) => {
      if (f.fieldUniqueName !== fieldUniqueName) {
        newSelectedFilters.push(f);
      } else {
        const newFilter: QueryFilter = {
          fieldUniqueName: f.fieldUniqueName,
          values: [],
        };

        f.values.forEach((existingValue) => {
          if (existingValue != value) {
            newFilter.values.push(existingValue);
          }
        });

        if (newFilter.values.length > 0) {
          newSelectedFilters.push(newFilter);
        }
      }
    });

    if (!disableQueryParamFiltering) {
      if (newSelectedFilters.length !== 0) {
        searchParams.set("filters", JSON.stringify(newSelectedFilters));
      } else {
        searchParams.delete("filters");
      }
    }

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    onFiltersChange(newSelectedFilters);
  };

  const onDismissFilter = (fieldUniqueName: string) => {
    const newSelectedFilters = selectedFilters.filter(
      (filter) => filter.fieldUniqueName !== fieldUniqueName
    );

    if (!disableQueryParamFiltering) {
      if (newSelectedFilters.length !== 0) {
        searchParams.set("filters", JSON.stringify(newSelectedFilters));
      } else {
        searchParams.delete("filters");
      }
    }

    setSearchParams(searchParams, {
      preventScrollReset: true,
    });

    onFiltersChange(newSelectedFilters);
  };

  if (!filters || loadingFilters) {
    return <QueryFilterTagsSkeleton />;
  }

  return (
    <div
      className={
        displayMode === "row"
          ? classes.queryFilterTagsContainer
          : classes.queryFilterTagsContainerGroup
      }
    >
      {selectedFilters.map((filter) => {
        const targetSearchFilter = filters.find(
          (f) => f.fieldUniqueName === filter.fieldUniqueName
        );
        const targetSearchFilterValues = cloneFilters(
          targetSearchFilter?.values ?? []
        );

        return (
          <div
            className={
              displayMode === "row"
                ? classes.tagGroupContainer
                : classes.tagGroupContainerGroup
            }
            key={filter.fieldUniqueName}
          >
            <div className={classes.filterHeaderGroup}>
              <Label
                weight="semibold"
                size={displayMode === "row" ? "medium" : "large"}
              >
                {targetSearchFilter?.fieldDisplayName}
                {displayMode === "row" ? ":" : ""}
              </Label>
              {displayMode === "grouped" && (
                <Button
                  appearance="subtle"
                  onClick={() => onDismissFilter(filter.fieldUniqueName)}
                  size="small"
                >
                  {t("clearFilter")}
                </Button>
              )}
            </div>
            <TagGroup
              onDismiss={(_ev, data) =>
                onDismissTag(filter.fieldUniqueName, data.value)
              }
              aria-label={targetSearchFilter?.fieldDisplayName}
              key={filter.fieldUniqueName}
              className={classes.tagGroup}
            >
              {filter.values.map((tag) => {
                const searchValue = targetSearchFilterValues.find(
                  (v) => v.value === tag
                );

                return (
                  <Tag
                    dismissible
                    dismissIcon={{ "aria-label": "remove" }}
                    value={tag}
                    key={tag}
                    size={displayMode === "row" ? "extra-small" : "medium"}
                  >
                    {searchValue?.displayValue || tag || t("emptyFilterValue")}
                  </Tag>
                );
              })}
            </TagGroup>
          </div>
        );
      })}
    </div>
  );
};
